import { useState } from 'react';
import { PlusIcon, TrashIcon, PlayIcon } from '@heroicons/react/24/outline';
import { cn } from '../../../lib/utils';
import {
  AiResponsibility,
  AiResponsibilitySchedule,
  AI_RESPONSIBILITY_INSTRUCTION_MAX_LENGTH,
  WEEKDAY_LABELS,
} from '../services/settingsService';
import {
  useResponsibilities,
  useCreateResponsibility,
  useUpdateResponsibility,
  useDeleteResponsibility,
  useRunResponsibilityNow,
} from '../hooks/useSettings';

const ALL_DAYS = [0, 1, 2, 3, 4, 5, 6];
const WEEKDAYS = [0, 1, 2, 3, 4];

function DayPicker({ days, onChange }: { days: number[]; onChange: (days: number[]) => void }) {
  const toggle = (d: number) =>
    onChange(days.includes(d) ? days.filter((x) => x !== d).sort() : [...days, d].sort());
  return (
    <div className="flex flex-wrap gap-1.5">
      {WEEKDAY_LABELS.map((label, d) => (
        <button
          key={d}
          type="button"
          onClick={() => toggle(d)}
          className={cn(
            'h-8 w-11 rounded-md text-xs font-medium transition-colors',
            days.includes(d)
              ? 'bg-brand text-white'
              : 'bg-surface-2 text-ink-2 hover:bg-black/[0.04]'
          )}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function ScheduleSummary({ schedule }: { schedule: AiResponsibilitySchedule }) {
  const days = schedule.days ?? [];
  let dayText: string;
  if (days.length === 7) dayText = 'every day';
  else if (days.length === 5 && WEEKDAYS.every((d) => days.includes(d))) dayText = 'weekdays';
  else dayText = days.map((d) => WEEKDAY_LABELS[d]).join(', ') || 'no days';
  return (
    <span>
      {schedule.time} · {dayText}
    </span>
  );
}

function ResponsibilityCard({ item }: { item: AiResponsibility }) {
  const update = useUpdateResponsibility();
  const remove = useDeleteResponsibility();
  const runNow = useRunResponsibilityNow();

  const [instruction, setInstruction] = useState(item.instruction);
  const [time, setTime] = useState(item.schedule.time);
  const [days, setDays] = useState<number[]>(item.schedule.days);

  const dirty =
    instruction !== item.instruction ||
    time !== item.schedule.time ||
    JSON.stringify(days) !== JSON.stringify(item.schedule.days);

  const canSave = dirty && instruction.trim().length > 0 && days.length > 0;

  const save = () =>
    update.mutate({ id: item.id, update: { instruction: instruction.trim(), schedule: { time, days } } });

  return (
    <div className="rounded-card border border-line bg-surface p-4 shadow-s">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 text-xs text-ink-3">
          <ScheduleSummary schedule={item.schedule} />
          {item.lastRunAt && (
            <span className="text-ink-3">
              · last run {new Date(item.lastRunAt).toLocaleString()}
              {item.lastRunStatus ? ` (${item.lastRunStatus})` : ''}
            </span>
          )}
        </div>
        <label className="flex cursor-pointer items-center gap-2 text-xs text-ink-2">
          <input
            type="checkbox"
            checked={item.enabled}
            onChange={(e) => update.mutate({ id: item.id, update: { enabled: e.target.checked } })}
            className="h-4 w-4 rounded border-line bg-surface text-brand focus:ring-brand/40"
          />
          {item.enabled ? 'Enabled' : 'Disabled'}
        </label>
      </div>

      <textarea
        value={instruction}
        onChange={(e) => setInstruction(e.target.value)}
        rows={2}
        maxLength={AI_RESPONSIBILITY_INSTRUCTION_MAX_LENGTH}
        className="w-full resize-none rounded-xl border border-line bg-surface px-3 py-2 text-sm text-ink focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
      />

      <div className="mt-3 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-xs text-ink-3">At</span>
          <input
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            className="rounded-xl border border-line bg-surface px-2 py-1 text-sm text-ink focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
          />
        </div>
        <DayPicker days={days} onChange={setDays} />
      </div>

      <div className="mt-4 flex items-center justify-between border-t border-line pt-3">
        <div className="flex gap-2">
          <button
            onClick={() => runNow.mutate(item.id)}
            disabled={runNow.isPending}
            className="flex items-center gap-1.5 rounded-pill bg-surface-2 px-2.5 py-1.5 text-xs font-medium text-ink-2 hover:bg-black/[0.04] disabled:opacity-50"
          >
            <PlayIcon className="h-3.5 w-3.5" />
            {runNow.isPending ? 'Running…' : 'Run now'}
          </button>
          <button
            onClick={() => remove.mutate(item.id)}
            className="flex items-center gap-1.5 rounded-pill px-2.5 py-1.5 text-xs font-medium text-danger hover:bg-danger/10"
          >
            <TrashIcon className="h-3.5 w-3.5" />
            Delete
          </button>
        </div>
        {dirty && (
          <button
            onClick={save}
            disabled={!canSave || update.isPending}
            className="rounded-pill bg-brand px-3 py-1.5 text-xs font-medium text-white hover:bg-brand/90 disabled:opacity-40"
          >
            {update.isPending ? 'Saving…' : 'Save changes'}
          </button>
        )}
      </div>
    </div>
  );
}

function AddResponsibility() {
  const create = useCreateResponsibility();
  const [instruction, setInstruction] = useState('');
  const [time, setTime] = useState('11:00');
  const [days, setDays] = useState<number[]>(ALL_DAYS);

  const canAdd = instruction.trim().length > 0 && days.length > 0;

  const add = () => {
    if (!canAdd) return;
    create.mutate(
      { instruction: instruction.trim(), schedule: { time, days }, enabled: true },
      {
        onSuccess: () => {
          setInstruction('');
          setTime('11:00');
          setDays(ALL_DAYS);
        },
      }
    );
  };

  return (
    <div className="rounded-card border border-dashed border-line-2 bg-surface p-4">
      <h3 className="mb-2 text-sm font-medium text-ink">Add a responsibility</h3>
      <textarea
        value={instruction}
        onChange={(e) => setInstruction(e.target.value)}
        rows={2}
        maxLength={AI_RESPONSIBILITY_INSTRUCTION_MAX_LENGTH}
        placeholder="e.g. List any order placed before 09:00 that isn't shipped yet, and post it to the team."
        className="w-full resize-none rounded-xl border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
      />
      <div className="mt-3 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-xs text-ink-3">At</span>
          <input
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            className="rounded-xl border border-line bg-surface px-2 py-1 text-sm text-ink focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
          />
        </div>
        <DayPicker days={days} onChange={setDays} />
        <button
          onClick={add}
          disabled={!canAdd || create.isPending}
          className="ml-auto flex items-center gap-1.5 rounded-pill bg-brand px-3 py-1.5 text-sm font-medium text-white hover:bg-brand/90 disabled:opacity-40"
        >
          <PlusIcon className="h-4 w-4" />
          Add
        </button>
      </div>
    </div>
  );
}

export default function AiResponsibilitiesSettings() {
  const { data: responsibilities, isLoading } = useResponsibilities();

  return (
    <div className="space-y-5">
      <div>
        <h2 className="font-display text-lg font-semibold text-ink">AI Responsibilities</h2>
        <p className="mt-1 text-sm text-ink-2">
          Give Pulse recurring duties in plain English. On the schedule you set, it uses your
          store's tools to check status (orders, appointments, and more) and posts the result to{' '}
          <span className="font-medium text-ink">Team Chat</span>.
        </p>
        <p className="mt-2 rounded-card border border-line bg-surface-2 px-3 py-2 text-xs text-ink-2">
          Reminders always post to Team Chat and email your staff. WhatsApp delivery is
          best-effort — reaching staff on WhatsApp reliably needs an approved message template.
        </p>
      </div>

      <AddResponsibility />

      {isLoading ? (
        <p className="text-sm text-ink-3">Loading…</p>
      ) : responsibilities && responsibilities.length > 0 ? (
        <div className="space-y-3">
          {responsibilities.map((item) => (
            <ResponsibilityCard key={item.id} item={item} />
          ))}
        </div>
      ) : (
        <p className="text-sm text-ink-3">
          No responsibilities yet. Add one above — for example, a morning check that all orders
          (or appointments) are on track.
        </p>
      )}
    </div>
  );
}
